/* eslint-disable no-underscore-dangle */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

import { ChangePasswordDto } from '../models/changePasswordDto';
import { UnitFamily } from '../models/enums/unit-families';
import { LoggedInUserDto } from '../models/loggedinUserDto';
import { LoginEditDto } from '../models/loginEditDTO';
import { RegisterDto } from '../models/registerDto';
import {
  ResetPasswordConfirmDto,
  ResetPasswordDto
} from '../models/resetPasswordDto';
import { Utilities } from '../shared/utils';
import { ApiService } from './api.service';
import { UserService } from './user.service';

// Convert to SecureStorage For Loading User/Auth
// only will need to be read once then memory via Observable will take over
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _isLoggedIn = new BehaviorSubject<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public isLoggedIn: Observable<boolean> = this._isLoggedIn;
  private user: LoggedInUserDto | null = null;


  private _unitFamily = new BehaviorSubject(UnitFamily.USCustomary);
  public unitFamily$: Observable<UnitFamily> = this._unitFamily;

  constructor(
    private api: ApiService,
    private router: Router,
    private userService: UserService
  ) {
    this.getUser().then(res => {
      this.user = res;
      this.setUnitFamily(res.unitFamily);
      if (res == null) {
        this._isLoggedIn.next(false);
        // TODO remove this, should come from AuthGuard
        return Promise.resolve(res);
        // return this.router.navigate(['/login']).then(() => res);
      }
      this._isLoggedIn.next(true);
      return res;
    }).catch(err => {
      console.error('Error getting user');
      this.user = null;
      this._isLoggedIn.next(false);
      // this.router.navigate(['/login']);
    });
  }

  isAuthenticated(): boolean {
    return this._isLoggedIn.getValue();
  }

  async getAuthHeaders(): Promise<string> {
    try {
      const user = await Utilities.storageGetItem('user') as LoggedInUserDto;
      if (user == null) { return Promise.reject('Not Logged in.') }
      const credentials = `Bearer ${user.authorization}`;
      return credentials;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }

  getUser(): Promise<LoggedInUserDto> {
    if (this.user != null) {
      return Promise.resolve(this.user);
    }
    return Utilities.storageGetItem('user');
  }

  setUser(user: LoggedInUserDto) {
    this.user = user;
    return Utilities.storageSetItem('user', user);
  }

  async setUnitFamily(value: UnitFamily) {
    await Utilities.storageSetItem('unitFamily', value);
    this._unitFamily?.next(value);
    const user = await this.getUser();
    if (user != null) {
      user.unitFamily = value;
      this.setUser(user);
    }
  }

  getUnitFamily(): UnitFamily {
    return this._unitFamily?.getValue();
  }

  login(loginDTO: LoginEditDto): Promise<LoggedInUserDto> {
    return this.api.post(`Login/authenticate`, loginDTO).toPromise()
      .then((res: LoggedInUserDto) => {
        this.setUser(res);
        this._isLoggedIn.next(true);
        return res;
      }).catch(err => {
        this._isLoggedIn.next(false);
        this.setUser(null);
        return Promise.reject(err);
      });
  }

  logout(): Promise<boolean> {
    return Utilities.storageRemoveItem('user').then(res => {
      this.user = null;
      this._isLoggedIn.next(false);
      return true;
    }).catch(err => {
      console.error('Logout failed', err);
      return Promise.reject(err);
    });
  }

  register(body: RegisterDto) {
    return this.api.post(`Login/signup`, body).toPromise();
  }

  resetPassword(body: ResetPasswordDto): Promise<boolean | void> {
    return this.api.post(`Login/resetPassword`, {}, { email: body.email }).toPromise();
  }

  confirmResetPassword(body: ResetPasswordConfirmDto) {
    return this.api.post(`Login/resetPasswordConfirmation`, {}, { token: body.token, newPassword: body.newPassword }).toPromise();
  }

  changePassword(body: ChangePasswordDto): Promise<boolean | void> {
    return this.api.post(`User/changePassword`, body).toPromise();
  }
}
